import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import PageContainer from '../../shared/pageContainer';
import CustomButton from '../../shared/customButton';
import NotificationCard from '../../shared/notificationCard';
import CustomFormDrawer from '../../shared/customFormDrawer';
import AlertDialog from '../../shared/alertDialog';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import CustomCheckbox from '../../shared/customCheckbox';
import { APP_PAGES } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { ORG_SETTINGS_NAVIGATION_ITEMS } from '../../../constants/organizationSettings';
import { getDocumentExpiryNotificationOptions } from '../../../constants/documentTypes';
import { sticky } from '../../../constants/helperCssRules';
import { documentCategoriesSelector } from '../../../store/selectors/documentCategoriesSelector';
import { isArrayEmpty, isObjectEmpty } from '../../../utility/helpers';
import { useTranslations } from '../../../utility/useTranslations';
import {
  getDocumentCategories,
  postDocumentCategories,
  patchDocumentCategories,
  deleteDocumentCategory,
  clearDocumentCategories,
} from '../../../store/modules/documentCategories';
import { showSuccessMessage } from '../../../utility/uiUtils';
import { hasAuthPermissionCheck } from '../../../utility/permissions';
import { prepareDocumentCategoryFields, prepareInitialData } from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  description: {
    paddingBottom: spacing(6),
    ...sticky(primary.white, 170),
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: spacing(6),
  },
  addCategoryIcon: {
    marginRight: spacing(1),
    width: 16,
    height: 16,
  },
  columnWrapper: {
    marginTop: spacing(6),
    paddingBottom: spacing(6),
    marginBottom: spacing(5),
    borderBottom: `1px solid ${primary.bluish7}`,
    display: 'flex',
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  labelWrapper: {
    width: 512,
    flexShrink: 0,
  },
  checkboxWrapper: {
    height: '100%',
    textAlign: 'center',
    display: 'grid',
    flex: '1 1 auto',
    minWidth: 0,
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 4,
  },
  categories: {
    marginBottom: spacing(7),
  },
  categoryNameWrapper: {
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
    marginBottom: spacing(6),
  },
  editButton: {
    maxHeight: 22,
    padding: spacing(1, 2, 1, 2),
    fontSize: 12,
    lineHeight: '14px',
    fontFamily: 'ProximaNova-Bold',
  },
  documentTypeList: {
    gap: 4,
    display: 'grid',
  },
  wrapper: {
    height: 40,
    alignItems: 'center',
    display: 'flex',
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  categoryForm: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    gridTemplateAreas: `
      "categoryName"
      "notifyBefore"
      "documentSubtitle"
      "documentTypes"
    `,
  },
  categoryName: {
    gridArea: 'categoryName',
    width: '50%',
  },
  notifyBefore: {
    gridArea: 'notifyBefore',
    width: '50%',
  },
  documentSubtitle: {
    gridArea: 'documentSubtitle',
  },
  documentTypes: {
    gridArea: 'documentTypes',
  },
  addCategoryButton: {
    padding: spacing(1, 3, 1, 3),
    maxHeight: 24,
    '& span': {
      fontSize: 12,
    },
  },
  checkboxPreviewWrapper: {
    backgroundColor: primary.bluish9,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    cursor: 'default',
  },
  customCheckbox: {
    cursor: 'default',
  },
}));

const OrganizationSettingsDocumentsPage = ({
  accessControl,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(
    APP_PAGES.ORGANIZATION_SETTINGS_DOCUMENTS
  );

  const { documentCategories } = useSelector(documentCategoriesSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [isCategoryDrawerOpened, setIsCategoryDrawerOpened] = useState(false);
  const [isDeleteCategoryDialogOpened, setIsDeleteCategoryDialogOpened] =
    useState(false);
  const [selectedCategory, setSelectedCategory] = useState({});

  const hasCategories = !isArrayEmpty(documentCategories);
  const isEdit = !isObjectEmpty(selectedCategory);
  const canAccessFeatures = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_FEATURES_MANAGE,
    accessControl
  );

  const canAccessPermissions = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_PERMISSIONS_MANAGE,
    accessControl
  );

  const canAccessProfileInfo = hasAuthPermissionCheck(
    PERMISSION_CODES.SETTINGS_PERSON_FIELDS_INFO_MANAGE,
    accessControl
  );

  const notifyBeforeOptions = useMemo(
    () =>
      getDocumentExpiryNotificationOptions(
        translations.categoryForm.formLabels.notifyBefore.options
      ),
    [translations]
  );

  const navigationItems = useMemo(() => {
    return ORG_SETTINGS_NAVIGATION_ITEMS.reduce((acc, navItem) => {
      if (
        (!canAccessFeatures && navItem.translationKey === 'features') ||
        (!canAccessProfileInfo && navItem.translationKey === 'profile-info') ||
        (!canAccessPermissions && navItem.translationKey === 'permissions')
      ) {
        return acc;
      }
      return [
        ...acc,
        {
          ...navItem,
          name: translations.navigationItems[navItem.translationKey],
        },
      ];
    }, []);
  }, [
    translations.navigationItems,
    canAccessFeatures,
    canAccessProfileInfo,
    canAccessPermissions,
  ]);

  const getInitialData = useCallback(async () => {
    await getDocumentCategories(dispatch);
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const cleanup = useCallback(() => {
    dispatch(clearDocumentCategories());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleAddCategory = () => {
    setIsCategoryDrawerOpened(true);
  };

  const handleCloseCategoryDrawer = () => {
    setIsCategoryDrawerOpened(false);
    setSelectedCategory({});
  };

  const handleSaveCategory = async values => {
    if (isEdit) {
      const updatedCategory = await patchDocumentCategories(dispatch, {
        ...values,
        selectedCategory,
      });
      showSuccessMessage(translations.successMessages.updatedCategory);
      return updatedCategory;
    }
    const createdCategory = await postDocumentCategories(dispatch, values);
    showSuccessMessage(translations.successMessages.createdCategory);
    return createdCategory;
  };

  const handleOpenDeleteCategoryDialog = () => {
    setIsDeleteCategoryDialogOpened(true);
  };

  const handleEditCategory = category => {
    setSelectedCategory(category);
    setIsCategoryDrawerOpened(true);
  };

  const onCloseDeleteCategory = () => setIsDeleteCategoryDialogOpened(false);

  const onDeleteCategory = async () => {
    setIsDeleteCategoryDialogOpened(false);
    setIsCategoryDrawerOpened(false);
    const updatedCategories = await deleteDocumentCategory(
      dispatch,
      selectedCategory.id
    );
    setSelectedCategory({});
    showSuccessMessage(translations.categoryDeleteDialog.deleteSuccess);
    return updatedCategories;
  };

  return (
    <PageContainer
      {...rest}
      translations={translations}
      accessControl={accessControl}
      navigationItems={navigationItems}
      dispatch={dispatch}
      shouldPassProps={false}
    >
      {!isLoading && (
        <div>
          <Typography className={classes.description} variant="body2">
            {translations.description}
          </Typography>
          <div className={classes.headerContent}>
            <CustomButton
              className={classes.addCategoryButton}
              customIconWrapper={classes.addCategoryIcon}
              type="addDarkRoundedOutlined"
              onClick={handleAddCategory}
            >
              {translations.addCategory}
            </CustomButton>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.labelWrapper}>
              <Typography variant="body2">{translations.nameLabel}</Typography>
            </div>
            <div className={classes.checkboxWrapper}>
              <Typography variant="body2">
                {translations.mandatoryLabel}
              </Typography>
              <Typography variant="body2">
                {translations.selfUploadLabel}
              </Typography>
              <Typography variant="body2">
                {translations.expiryDateLabel}
              </Typography>
            </div>
          </div>
          {hasCategories ? (
            documentCategories.map((category, idx) => {
              return (
                <div key={`category_${idx}`} className={classes.categories}>
                  <div className={classes.categoryNameWrapper}>
                    <Typography variant="h5">{category.name}</Typography>
                    <CustomButton
                      className={classes.editButton}
                      onClick={() => handleEditCategory(category)}
                      type="withTextLightRounded"
                    >
                      {translations.edit}
                    </CustomButton>
                  </div>
                  <div className={classes.documentTypeList}>
                    {category.types?.map((documentType, index) => {
                      return (
                        <div
                          key={`document_type_${index}`}
                          className={classes.wrapper}
                        >
                          <div className={classes.labelWrapper}>
                            <TextBoxWithTooltip
                              variant="subtitle2"
                              text={documentType.name}
                            />
                          </div>
                          <div className={classNames(classes.checkboxWrapper)}>
                            <div className={classes.checkboxPreviewWrapper}>
                              <CustomCheckbox
                                customCheckBoxRoot={classes.customCheckbox}
                                customCheckboxClass={classes.customCheckbox}
                                customCheckedCheckboxClass={
                                  classes.customCheckbox
                                }
                                isChecked={documentType.is_mandatory}
                                onChange={() => {}}
                                disabled
                              />
                            </div>
                            <div className={classes.checkboxPreviewWrapper}>
                              <CustomCheckbox
                                customCheckBoxRoot={classes.customCheckbox}
                                customCheckboxClass={classes.customCheckbox}
                                customCheckedCheckboxClass={
                                  classes.customCheckbox
                                }
                                isChecked={documentType.self_upload}
                                onChange={() => {}}
                                disabled
                              />
                            </div>
                            <div className={classes.checkboxPreviewWrapper}>
                              <CustomCheckbox
                                customCheckBoxRoot={classes.customCheckbox}
                                customCheckboxClass={classes.customCheckbox}
                                customCheckedCheckboxClass={
                                  classes.customCheckbox
                                }
                                isChecked
                                onChange={() => {}}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <NotificationCard
              title={translations.noResults.title}
              content={translations.noResults.content}
            />
          )}
        </div>
      )}
      <CustomFormDrawer
        customContentClass={classes.categoryForm}
        translations={translations.categoryForm}
        isOpened={isCategoryDrawerOpened}
        isInitialValid={isEdit}
        initialData={prepareInitialData(selectedCategory)}
        fields={prepareDocumentCategoryFields(classes)}
        notifyBeforeOptions={notifyBeforeOptions}
        onClose={handleCloseCategoryDrawer}
        onSave={handleSaveCategory}
        onDelete={handleOpenDeleteCategoryDialog}
        isMiddleWidth
        hasCancelButton
      />
      <AlertDialog
        translations={translations.categoryDeleteDialog}
        isOpened={isDeleteCategoryDialogOpened}
        onClose={onCloseDeleteCategory}
        onConfirm={onDeleteCategory}
        isWarning
      />
    </PageContainer>
  );
};

OrganizationSettingsDocumentsPage.propTypes = {
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(OrganizationSettingsDocumentsPage);
